import { useMemo } from 'react';
import { Box } from '@mui/material';
import {
    Visibility,
    NotificationsNone,
    Description,
    ChatBubbleOutlineOutlined,
    AttachMoney,
    Send as SendIcon,
} from '@mui/icons-material';
import { AlertsData, OverdueDocument, DocumentNeedingFix } from 'hooks/analytics';
import { SectionCard, SectionTitle } from '../../components/analyticsUi';
import InfoTooltip from 'components/v2/common/infoTooltip';
import AlertCard from '../../components/AlertCard';
import type { AlertDetailGroup } from '../../components/AlertCard';
import ClickableName from '../../components/ClickableName';
import ActionMenu from '../../components/ActionMenu';
import { AnalyticsActions } from '../../hooks/useAnalyticsActions';
import { formatCurrency } from 'utils/formatters';
import {
    TODAY_NEEDS_ATTENTION,
    TODAY_OVERDUE_DOCUMENTS,
    TODAY_DOCUMENTS_NEEDING_FIXES,
    TODAY_UNPAID_CONTRACTS,
    TODAY_UNCONVERTED_OFFERS,
} from '../../constants/metricDescriptions';

interface AlertsSectionProps {
    alerts: AlertsData;
    actions: AnalyticsActions;
    isOwner: boolean;
}

/** Group documents by clientId, sorted by worst overdue first */
function groupOverdueByClient(
    docs: OverdueDocument[],
    actions: AnalyticsActions,
    isOwner: boolean
): AlertDetailGroup[] {
    const map = new Map<number, OverdueDocument[]>();
    for (const d of docs) {
        const list = map.get(d.clientId) || [];
        list.push(d);
        map.set(d.clientId, list);
    }

    return Array.from(map.entries())
        .sort((a, b) => {
            const maxA = Math.max(...a[1].map((d) => d.daysOverdue));
            const maxB = Math.max(...b[1].map((d) => d.daysOverdue));
            return maxB - maxA;
        })
        .map(([clientId, items]) => ({
            groupKey: String(clientId),
            groupLabel: (
                <ClickableName onClick={() => actions.navigateToClient(clientId)}>
                    {items[0].candidateName}
                </ClickableName>
            ),
            itemCount: items.length,
            groupActions: (
                <ActionMenu
                    isOwner={isOwner}
                    items={[
                        {
                            label: 'View Documents',
                            icon: <Description sx={{ fontSize: 14 }} />,
                            onClick: () =>
                                actions.navigateToDocuments(clientId, items[0].applicationId),
                        },
                        {
                            label: 'Message Client',
                            icon: <ChatBubbleOutlineOutlined sx={{ fontSize: 14 }} />,
                            onClick: () =>
                                actions.openMessageComposer(
                                    items[0].chatGroupId,
                                    items[0].candidateName,
                                    'overdue documents'
                                ),
                        },
                    ]}
                />
            ),
            items: items.map((d) => ({
                label: <span>{d.documentName}</span>,
                value: `${d.daysOverdue}d · ${d.status.replace(/_/g, ' ')}`,
                actions: (
                    <ActionMenu
                        isOwner={isOwner}
                        items={[
                            {
                                label: 'Send Reminder',
                                icon: <NotificationsNone sx={{ fontSize: 14 }} />,
                                onClick: () =>
                                    actions.sendReminder(d.candidateName, d.applicationId, d.docId),
                            },
                            {
                                label: 'View Documents',
                                icon: <Description sx={{ fontSize: 14 }} />,
                                onClick: () =>
                                    actions.navigateToDocuments(d.clientId, d.applicationId),
                            },
                        ]}
                    />
                ),
            })),
        }));
}

/** Group fix-needed documents by clientId */
function groupFixesByClient(
    docs: DocumentNeedingFix[],
    actions: AnalyticsActions,
    isOwner: boolean
): AlertDetailGroup[] {
    const map = new Map<number, DocumentNeedingFix[]>();
    for (const d of docs) {
        const list = map.get(d.clientId) || [];
        list.push(d);
        map.set(d.clientId, list);
    }

    return Array.from(map.entries())
        .sort((a, b) => {
            const maxA = Math.max(...a[1].map((d) => d.daysSinceFlagged));
            const maxB = Math.max(...b[1].map((d) => d.daysSinceFlagged));
            return maxB - maxA;
        })
        .map(([clientId, items]) => ({
            groupKey: String(clientId),
            groupLabel: (
                <ClickableName onClick={() => actions.navigateToClient(clientId)}>
                    {items[0].candidateName}
                </ClickableName>
            ),
            itemCount: items.length,
            groupActions: (
                <ActionMenu
                    isOwner={isOwner}
                    items={[
                        {
                            label: 'View Documents',
                            icon: <Description sx={{ fontSize: 14 }} />,
                            onClick: () =>
                                actions.navigateToDocuments(clientId, items[0].applicationId),
                        },
                        {
                            label: 'Message Client',
                            icon: <ChatBubbleOutlineOutlined sx={{ fontSize: 14 }} />,
                            onClick: () =>
                                actions.openMessageComposer(
                                    items[0].chatGroupId,
                                    items[0].candidateName,
                                    'documents needing fixes'
                                ),
                        },
                    ]}
                />
            ),
            items: items.map((d) => ({
                label: <span>{d.documentName}</span>,
                value: `${d.daysSinceFlagged}d since flagged · ${d.documentType}`,
                actions: (
                    <ActionMenu
                        isOwner={isOwner}
                        items={[
                            {
                                label: 'Send Reminder',
                                icon: <NotificationsNone sx={{ fontSize: 14 }} />,
                                onClick: () =>
                                    actions.sendReminder(d.candidateName, d.applicationId, d.docId),
                            },
                            {
                                label: 'View Documents',
                                icon: <Description sx={{ fontSize: 14 }} />,
                                onClick: () =>
                                    actions.navigateToDocuments(d.clientId, d.applicationId),
                            },
                        ]}
                    />
                ),
            })),
        }));
}

const AlertsSection = ({ alerts, actions, isOwner }: AlertsSectionProps) => {
    const totalOverdue = alerts.overdueDocuments.length;
    const clientsWithOverdue = new Set(alerts.overdueDocuments.map((d) => d.clientId)).size;
    const redOverdue = alerts.overdueDocuments.filter((d) => d.daysOverdue >= 14).length;

    const totalFixes = alerts.documentsNeedingFixes.length;
    const clientsWithFixes = new Set(alerts.documentsNeedingFixes.map((d) => d.clientId)).size;

    const totalAgingAmount = alerts.agingPayments.reduce((sum, p) => sum + p.amountOutstanding, 0);
    const criticalAging = alerts.agingPayments.filter((p) => p.daysSinceOffer >= 60).length;

    const totalStaleAmount = alerts.staleOffers.reduce((sum, o) => sum + o.offerAmount, 0);
    const redStale = alerts.staleOffers.filter((o) => o.daysSinceOfferSent >= 14).length;

    const overdueGroups = useMemo(
        () => groupOverdueByClient(alerts.overdueDocuments, actions, isOwner),
        [alerts.overdueDocuments, actions, isOwner]
    );

    const fixGroups = useMemo(
        () => groupFixesByClient(alerts.documentsNeedingFixes, actions, isOwner),
        [alerts.documentsNeedingFixes, actions, isOwner]
    );

    return (
        <SectionCard>
            <SectionTitle>
                Needs Your Attention
                <InfoTooltip title={TODAY_NEEDS_ATTENTION} />
            </SectionTitle>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <AlertCard
                    severity={redOverdue > 0 ? 'red' : totalOverdue > 0 ? 'amber' : 'green'}
                    icon="document"
                    title={`${totalOverdue} documents overdue across ${clientsWithOverdue} clients`}
                    subtitle="No status update for 7+ days"
                    count={totalOverdue}
                    tooltip={TODAY_OVERDUE_DOCUMENTS}
                    groups={overdueGroups}
                />

                <AlertCard
                    severity={totalFixes > 0 ? 'amber' : 'green'}
                    icon="fix"
                    title={`${totalFixes} documents need fixes across ${clientsWithFixes} clients`}
                    subtitle="Blocking case progress"
                    count={totalFixes}
                    tooltip={TODAY_DOCUMENTS_NEEDING_FIXES}
                    groups={fixGroups}
                />

                <AlertCard
                    severity={criticalAging > 0 ? 'red' : totalAgingAmount > 0 ? 'amber' : 'green'}
                    icon="payment"
                    title={`${alerts.agingPayments.length} contracts signed but unpaid for 30+ days (${formatCurrency(totalAgingAmount)})`}
                    subtitle="Active contracts with outstanding balances"
                    count={alerts.agingPayments.length}
                    tooltip={TODAY_UNPAID_CONTRACTS}
                    details={alerts.agingPayments.map((p) => ({
                        label: (
                            <span>
                                <ClickableName onClick={() => actions.navigateToClient(p.clientId)}>
                                    {p.candidateName}
                                </ClickableName>
                                {' — '}
                                {p.programName}
                            </span>
                        ),
                        value: `${formatCurrency(p.amountOutstanding)} · ${p.daysSinceOffer}d since offer`,
                        actions: (
                            <ActionMenu
                                isOwner={isOwner}
                                items={[
                                    {
                                        label: 'View Contract',
                                        icon: <Visibility sx={{ fontSize: 14 }} />,
                                        onClick: () =>
                                            actions.navigateToContract(p.clientId, p.applicationId),
                                    },
                                    {
                                        label: 'Mark Paid',
                                        icon: <AttachMoney sx={{ fontSize: 14 }} />,
                                        onClick: () =>
                                            actions.markPaid(
                                                p.candidateName,
                                                p.applicationId,
                                                p.milestoneId
                                            ),
                                    },
                                    {
                                        label: 'Resend Invoice',
                                        icon: <SendIcon sx={{ fontSize: 14 }} />,
                                        onClick: () =>
                                            actions.resendInvoice(
                                                p.candidateName,
                                                p.applicationId,
                                                p.milestoneId
                                            ),
                                    },
                                    {
                                        label: 'Message Client',
                                        icon: <ChatBubbleOutlineOutlined sx={{ fontSize: 14 }} />,
                                        onClick: () =>
                                            actions.openMessageComposer(
                                                p.chatGroupId,
                                                p.candidateName,
                                                'outstanding payment'
                                            ),
                                    },
                                ]}
                            />
                        ),
                    }))}
                />

                <AlertCard
                    severity={
                        redStale > 0 ? 'red' : alerts.staleOffers.length > 0 ? 'amber' : 'green'
                    }
                    icon="offer"
                    title={`${alerts.staleOffers.length} offers sent 14+ days ago (${formatCurrency(totalStaleAmount)})`}
                    subtitle="Potential revenue slipping away"
                    count={alerts.staleOffers.length}
                    tooltip={TODAY_UNCONVERTED_OFFERS}
                    details={alerts.staleOffers.map((o) => ({
                        label: (
                            <span>
                                <ClickableName onClick={() => actions.navigateToClient(o.clientId)}>
                                    {o.candidateName}
                                </ClickableName>
                                {' — '}
                                {o.programName}
                            </span>
                        ),
                        value: `${formatCurrency(o.offerAmount)} · ${o.daysSinceOfferSent}d since sent`,
                        actions: (
                            <ActionMenu
                                isOwner={isOwner}
                                items={[
                                    {
                                        label: 'View Case',
                                        icon: <Visibility sx={{ fontSize: 14 }} />,
                                        onClick: () => actions.navigateToClient(o.clientId),
                                    },
                                    {
                                        label: 'Resend Invoice',
                                        icon: <SendIcon sx={{ fontSize: 14 }} />,
                                        onClick: () =>
                                            actions.resendInvoice(
                                                o.candidateName,
                                                o.applicationId,
                                                o.milestoneId
                                            ),
                                    },
                                    {
                                        label: 'Message Client',
                                        icon: <ChatBubbleOutlineOutlined sx={{ fontSize: 14 }} />,
                                        onClick: () =>
                                            actions.openMessageComposer(
                                                o.chatGroupId,
                                                o.candidateName,
                                                'your offer'
                                            ),
                                    },
                                ]}
                            />
                        ),
                    }))}
                />
            </Box>
        </SectionCard>
    );
};

export default AlertsSection;
